import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { SessionStorageService } from '@app/services/session-storage.service';
import { MsalService } from '@azure/msal-angular';
import { catchError, from, switchMap, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionStorageService = inject(SessionStorageService);
  const msalService = inject(MsalService);
  const errorHandler = (error: HttpErrorResponse) => {
    if (error.status === 401 || error.status === 403) {
      const account = msalService.instance.getActiveAccount();
      if (account) {
        return from(msalService.acquireTokenSilent({ scopes: ['user.read'], account, forceRefresh: true })
          .pipe(
            switchMap((res) => {
              sessionStorageService.setItem('accessToken', res.accessToken);
              sessionStorageService.setItem('idToken', res.idToken);
              const clonedRequest = req.clone({
                headers: req.headers.append('Authorization', `Bearer ${res.idToken}`),
              });
              return next(clonedRequest); // Retry API when failed because of token expire.
            }),
            catchError((error) => {
              msalService.loginRedirect({ scopes: ['user.read'] });
              return throwError(() => error);
            })
          ))
      }
    }
    return throwError(() => error);
  };

  // Check if 'skipAuth' is set, and bypass token addition
  if (req.headers.has('skipAuth')) {
    const clonedRequest = req.clone({
      headers: req.headers.delete('skipAuth'), // Remove 'skipAuth' before sending
    });
    return next(clonedRequest);
  }

  else if (req.headers.has('accessToken')) {
    const token = sessionStorageService.getItem('accessToken');
    const clonedRequest = req.clone({
      headers: req.headers.delete('accessToken').append('Authorization', `Bearer ${token}`),
    });
    return next(clonedRequest).pipe(
      catchError(errorHandler)
    );
  }

  const token = sessionStorageService.getItem('idToken');
  const clonedRequest = req.clone({
    headers: req.headers.append('Authorization', `Bearer ${token}`),
  });

  return next(clonedRequest).pipe(
    catchError(errorHandler)
  );
};
